/* IMPORTS */
import React, { useCallback, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import { CorrelationToolDataSelection } from "./CorrelationToolDataSelection";
import { ViewCorrelations } from "./ViewCorrelations";
import { Spinner } from "react-bootstrap";
import { ViewMatrix } from "./ViewMatrix";
import { ViewRawData } from "./ViewRawData";
import "./CorrelationStyling.css";
import gettext from "../../../shared/utils/gettext";
import {
  BestCorTypes,
  CorDetailsItemTypes,
  CorDetailsTypes,
  DatasetTypes,
  MatrixTypes,
  MeasurementTypes,
  RawDataTypes,
  RoiTypes,
} from "./ObjectInterfaces";
import cropr, { GeometryModel } from "../../../shared/utils/cropr";

/* Assigning the prop types */
interface CorrelationToolMenuProps {
  correlations: BestCorTypes[];
  clearMeasurementLayers: any;
  datasets: DatasetTypes[];
  did: string;
  matrixData: MatrixTypes;
  measurementDetails: CorDetailsTypes;
  onClick: any;
  rawData: RawDataTypes;
  setStartTool: any;
  startTool: boolean;
  toggleMeasurementLayer: any;
}

/* Local type interfaces */
interface IsobusTypes {
  abbreviation?: string;
  description: string;
  group?: string;
  name: string;
  priority: string;
  unit?: string;
}

interface VisualizedMeasurementTypes {
  uuid: string;
  index: number;
  name: string;
}

/* Function used to assign the correct name and unit to a measurement */
const getDisplayName = (code: string, name: string, unit?: string) => {
  if (unit) {
    if (unit !== "") {
      unit = `(${unit})`;
    }
  } else {
    unit = "";
  }

  const isobus: IsobusTypes =
    // @ts-ignore   [Proper TS Solution Needed]
    cropr.isobus_measurement_types[code] ||
    // @ts-ignore   [Proper TS Solution Needed]
    cropr.isobus_measurement_types[name] ||
    // @ts-ignore   [Proper TS Solution Needed]
    cropr.isobus_measurement_types[name.toLowerCase()];
  const displayName: string = isobus
    ? isobus.unit
      ? `${isobus.name} (${isobus.unit})`
      : `${isobus.name} ${unit}`
    : `${name} ${unit}`;
  return displayName;
};

/* CorrelationToolMenu Component */
export const CorrelationToolMenu = ({
  correlations,
  clearMeasurementLayers,
  datasets,
  did,
  matrixData,
  measurementDetails,
  onClick,
  rawData,
  startTool,
  setStartTool,
  toggleMeasurementLayer,
}: CorrelationToolMenuProps) => {
  const [key, setKey] = useState<string | null>("Correlations");

  /* Function collecting the relevant image data for both measurements */
  const getImageData = useCallback(
    (measurement) => {
      let dataset: DatasetTypes = datasets.filter(
        (ds: DatasetTypes) => ds.uuid === measurement.uuid
      )[0];
      if (!dataset.layers) {
        dataset.layers = [];
        dataset.data_set_rois.forEach(function (dsr: RoiTypes) {
          let cropfield: RoiTypes | GeometryModel = cropr.cropfields.findWhere({ did: dsr.roi });
          if (cropfield) {
            dsr.cropfield = cropfield;
            dataset.layers!.push(dsr);
          }
        });
      }
      let layersArr: RoiTypes[] = dataset.layers;
      layersArr = layersArr.filter((l) => l.roi === did);
      let layer: RoiTypes = layersArr[0];
      let ms: MeasurementTypes;
      if (measurement.index === null) {
        ms = dataset.dataset_measurements.filter(
          (ms: MeasurementTypes) => ms.name === measurement.name
        )[0];
      } else {
        ms = dataset.dataset_measurements.filter(
          (ms: MeasurementTypes) => ms.index === measurement.index
        )[0];
      }

      const name: string = ms.name.split("__")[0];
      ms.display_name = getDisplayName(ms.code, name, ms.unit);
      toggleMeasurementLayer(dataset, ms, layer);
    },
    [datasets, did, toggleMeasurementLayer]
  );

  /* Function used to visualize the measurements within a correlation on the map */
  const visualizeCorrelation = useCallback(
    async (var1Details: CorDetailsItemTypes, var2Details: CorDetailsItemTypes) => {
      await clearMeasurementLayers();
      setStartTool(!startTool);
      let measurement1: VisualizedMeasurementTypes = {
        uuid: var1Details.datasetId,
        index: var1Details.index,
        name: var1Details.name,
      };
      let measurement2: VisualizedMeasurementTypes = {
        uuid: var2Details.datasetId,
        index: var2Details.index,
        name: var2Details.name,
      };

      if (JSON.stringify(measurement1) === JSON.stringify(measurement2)) {
        await getImageData(measurement1);
      } else {
        await getImageData(measurement1);
        await getImageData(measurement2);
      }
    },
    [clearMeasurementLayers, getImageData, setStartTool, startTool]
  );

  /* JSX */
  return (
    <Modal show={true} onHide={() => setStartTool(!startTool)} dialogClassName="modal-xl">
      <Modal.Header closeButton>
        <Modal.Title>{gettext("The Correlation Tool")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {correlations && matrixData && rawData && measurementDetails ? (
          <Tabs activeKey={key} onSelect={(key: string | null) => setKey(key)}>
            {correlations.length > 0 ? (
              <Tab eventKey="Correlations" title={gettext("Correlations")}>
                <ViewCorrelations
                  correlations={correlations}
                  getDisplayName={getDisplayName}
                  measurementDetails={measurementDetails!}
                  visualizeCorrelation={visualizeCorrelation}
                />
              </Tab>
            ) : (
              <Tab eventKey="Correlations" title={gettext("Correlations")}>
                <div className="bg-white">
                  <div className="alert alert-info pt-2 mt-2">
                    {gettext("No Correlations were found...")}
                  </div>
                </div>
              </Tab>
            )}
            {Object.keys(matrixData).length && (
              <Tab eventKey="Matrix" title={gettext("Matrix")}>
                <ViewMatrix
                  getDisplayName={getDisplayName}
                  matrixData={matrixData}
                  measurementDetails={measurementDetails}
                  visualizeCorrelation={visualizeCorrelation}
                />
              </Tab>
            )}
            {Object.keys(rawData).length && (
              <Tab eventKey="Raw Data" title={gettext("Raw Data")}>
                <ViewRawData getDisplayName={getDisplayName} rawData={rawData} />
              </Tab>
            )}
            <Tab eventKey="Custom Correlations" title={gettext("Custom Correlations")}>
              <CorrelationToolDataSelection
                datasets={datasets}
                did={did}
                getDisplayName={getDisplayName}
                onClick={onClick}
                setKey={setKey}
              />
            </Tab>
          </Tabs>
        ) : (
          <div className="mx-auto text-center">
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
            <h3>{gettext("Correlations are being calculated, this could take a moment...")}</h3>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
